/**
 * POS Category Navigation Component
 *
 * Hierarchical drill-down navigation:
 * Family → Category → Animal Type
 */

import React, { useState } from 'react';
import {
  FEED_FAMILIES,
  FEED_CATEGORY_FAMILIES,
} from '../../constants/posCategoryFamilies';
import { getCategoryColor } from '../../constants/posColors';

export type NavLevel = 'family' | 'category' | 'animal';

export interface NavState {
  level: NavLevel;
  selectedFamily: string | null;
  selectedCategory: string | null;
  selectedAnimalType: string | null;
}

interface POSCategoryNavProps {
  navState: NavState;
  availableAnimalTypes: string[];  // Dynamic list based on actual products
  onSelectFamily: (family: string) => void;
  onSelectCategory: (category: string) => void;
  onSelectAnimalType: (animalType: string) => void;
  onBack: () => void;
  onShowAll: () => void;
}

const POSCategoryNav: React.FC<POSCategoryNavProps> = ({
  navState,
  availableAnimalTypes,
  onSelectFamily,
  onSelectCategory,
  onSelectAnimalType,
  onBack,
  onShowAll,
}) => {
  const [hoveredButton, setHoveredButton] = useState<string | null>(null);

  // Render family-level buttons
  const renderFamilyButtons = () => {
    const families = Object.entries(FEED_FAMILIES);

    return (
      <div style={styles.navContainer}>
        <div style={styles.navHeader}>
          <span style={styles.navTitle}>Select Category</span>
          <button
            onClick={onShowAll}
            style={styles.showAllButton}
            onMouseEnter={() => setHoveredButton('show-all')}
            onMouseLeave={() => setHoveredButton(null)}
          >
            Show All Products
          </button>
        </div>
        <div style={styles.familyGrid}>
          {families.map(([name, data]) => (
            <button
              key={name}
              onClick={() => onSelectFamily(name)}
              style={{
                ...styles.familyButton,
                backgroundColor: hoveredButton === name ? data.hoverColor : data.color,
              }}
              onMouseEnter={() => setHoveredButton(name)}
              onMouseLeave={() => setHoveredButton(null)}
            >
              <span style={styles.familyIcon}>{data.icon}</span>
              <span style={styles.familyName}>{name}</span>
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Render category-level buttons
  const renderCategoryButtons = () => {
    if (!navState.selectedFamily) return null;

    const familyData = FEED_FAMILIES[navState.selectedFamily];
    if (!familyData) return null;

    return (
      <div style={styles.navContainer}>
        <div style={styles.navHeader}>
          <button
            onClick={onBack}
            style={styles.backButton}
            onMouseEnter={() => setHoveredButton('back')}
            onMouseLeave={() => setHoveredButton(null)}
          >
            ← Back
          </button>
          <span style={styles.breadcrumb}>
            <span style={{ color: familyData.color }}>{familyData.icon}</span>
            {' '}{navState.selectedFamily}
          </span>
        </div>
        <div style={styles.categoryGrid}>
          {familyData.categories.map((category) => {
            const colors = getCategoryColor(category);
            return (
              <button
                key={category}
                onClick={() => onSelectCategory(category)}
                style={{
                  ...styles.categoryButton,
                  backgroundColor: hoveredButton === category ? colors.hover : colors.bg,
                  color: colors.text,
                }}
                onMouseEnter={() => setHoveredButton(category)}
                onMouseLeave={() => setHoveredButton(null)}
              >
                {category}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Render animal type buttons (only for feed categories with multiple types)
  const renderAnimalTypeButtons = () => {
    if (!navState.selectedFamily || !navState.selectedCategory) return null;

    const familyData = FEED_FAMILIES[navState.selectedFamily];
    const categoryColors = getCategoryColor(navState.selectedCategory);

    // If only "All" is available (or empty), don't show animal type buttons
    // Products will be shown directly
    if (availableAnimalTypes.length <= 1) {
      return null;
    }

    return (
      <div style={styles.navContainer}>
        <div style={styles.navHeader}>
          <button
            onClick={onBack}
            style={styles.backButton}
            onMouseEnter={() => setHoveredButton('back')}
            onMouseLeave={() => setHoveredButton(null)}
          >
            ← Back
          </button>
          <span style={styles.breadcrumb}>
            <span style={{ color: familyData?.color }}>{familyData?.icon}</span>
            {' '}{navState.selectedFamily} › {navState.selectedCategory}
          </span>
          <span style={styles.typeCount}>
            {availableAnimalTypes.length - 1} types
          </span>
        </div>
        <div style={styles.animalGrid}>
          {availableAnimalTypes.map((animalType) => {
            const isSelected = navState.selectedAnimalType === animalType;
            return (
              <button
                key={animalType}
                onClick={() => onSelectAnimalType(animalType)}
                style={{
                  ...styles.animalButton,
                  backgroundColor: isSelected
                    ? categoryColors.bg
                    : hoveredButton === animalType
                      ? '#E5E7EB'
                      : '#F3F4F6',
                  color: isSelected ? categoryColors.text : '#1F2937',
                  borderColor: categoryColors.bg,
                }}
                onMouseEnter={() => setHoveredButton(animalType)}
                onMouseLeave={() => setHoveredButton(null)}
              >
                {animalType}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // Render based on current navigation level
  switch (navState.level) {
    case 'family':
      return renderFamilyButtons();
    case 'category':
      return renderCategoryButtons();
    case 'animal':
      return renderAnimalTypeButtons();
    default:
      return renderFamilyButtons();
  }
};

const styles: Record<string, React.CSSProperties> = {
  navContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    padding: '10px',
    marginBottom: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  navHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '8px',
  },
  navTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  showAllButton: {
    padding: '6px 14px',
    backgroundColor: '#6B7280',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  backButton: {
    padding: '6px 14px',
    backgroundColor: '#F3F4F6',
    color: '#1F2937',
    border: '2px solid #D1D5DB',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  breadcrumb: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1F2937',
    flex: 1,
  },
  typeCount: {
    fontSize: '14px',
    color: '#6B7280',
    fontWeight: '500',
  },
  familyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
    gap: '8px',
  },
  familyButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '10px 12px',
    minHeight: '56px',
    border: 'none',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'background-color 0.15s, transform 0.1s',
    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
  },
  familyIcon: {
    fontSize: '20px',
  },
  familyName: {
    fontSize: '14px',
    fontWeight: '700',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  categoryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '8px',
  },
  categoryButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px',
    minHeight: '44px',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.15s, transform 0.1s',
    boxShadow: '0 2px 4px rgba(0,0,0,0.12)',
    textAlign: 'center',
  },
  animalGrid: {
    display: 'flex',
    overflowX: 'auto',
    gap: '8px',
    flexWrap: 'nowrap',
  },
  animalButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 14px',
    minHeight: '32px',
    border: '2px solid',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.15s',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
};

export default POSCategoryNav;
